import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api-client';
import { apiErrorMessage } from '@/lib/api-error';
import { formatUZS } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, Edit2, Trash2, Upload, Download, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { PaginationControls } from '@/components/PaginationControls';
import ProductImportDialog from '@/components/products/ProductImportDialog';
import { useCategories } from '@/hooks/use-categories';
import { useGlobalProducts, type GlobalProduct } from '@/hooks/use-global-products';
import {
  validateProductForm, emptyProductForm, formToPayload, productToForm, PRODUCT_TENORS, PRODUCT_UNITS,
  parseTenors, formatTenors, downloadProductTemplate, readProductsExcelWithCsv, type ImportRow,
} from '@/lib/product-schema';

const PAGE_SIZE = 50;

/** FR-98: Lovable's admin «Global mahsulotlar» tab — CRUD, Excel import, template. */
export default function GlobalProducts() {
  const { t, i18n } = useTranslation();
  const locale = ({ uz: 'uz-UZ', ru: 'ru-RU', en: 'en-US' } as Record<string, string>)[(i18n.language || 'uz').slice(0, 2)] ?? 'uz-UZ';
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [guruhFilter, setGuruhFilter] = useState('all');
  const [infoProduct, setInfoProduct] = useState<GlobalProduct | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyProductForm);
  const [saving, setSaving] = useState(false);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const [importRows, setImportRows] = useState<ImportRow[] | null>(null);
  const [importCsv, setImportCsv] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  const { data: categories = [] } = useCategories();
  const { data, isLoading } = useGlobalProducts({ search: debouncedSearch, category: catFilter, guruh_nomi: guruhFilter, page, per_page: PAGE_SIZE });
  const products = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.lastPage ?? 1;
  const guruhOptions = data?.groups ?? [];
  const activeCategories = categories.map(c => String(c.name));
  const uniqueCategories = [...new Set(products.map(p => p.category).filter(Boolean))] as string[];
  const filterCategories = [...new Set([...activeCategories, ...uniqueCategories])].sort((a, b) => a.localeCompare(b, 'uz'));

  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { setPage(1); }, [catFilter, guruhFilter]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['/global-products'] });

  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(emptyProductForm); };

  const openEdit = (p: GlobalProduct) => {
    setEditingId(p.id);
    setForm(productToForm(p));
    setShowForm(true);
  };

  const saveProduct = async () => {
    const formErrors = validateProductForm(form);
    if (Object.keys(formErrors).length > 0) {
      toast.error(t('mkt_global_products.requiredFields', { fields: Object.values(formErrors).join(', ') }));
      return;
    }

    setSaving(true);
    try {
      const { sku, ...attributes } = formToPayload(form);
      if (editingId) {
        const { error } = await apiClient.PATCH('/admin/global-products/{globalProduct}', {
          params: { path: { globalProduct: editingId } },
          body: attributes as never,
        });
        if (error) throw new Error(apiErrorMessage(error, t('mkt_global_products.error')));
        toast.success(t('mkt_global_products.updated'));
      } else {
        const { error } = await apiClient.POST('/admin/global-products', { body: { sku, ...attributes } as never });
        if (error) throw new Error(apiErrorMessage(error, t('mkt_global_products.error')));
        toast.success(t('mkt_global_products.added'));
      }
      closeForm();
      refresh();
    } catch (err) {
      toast.error((err as Error)?.message || t('mkt_global_products.error'));
    } finally { setSaving(false); }
  };

  const deleteProduct = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      const { error } = await apiClient.DELETE('/admin/global-products/{globalProduct}', {
        params: { path: { globalProduct: deleteId } },
      });
      if (error) throw new Error(apiErrorMessage(error, t('mkt_global_products.error')));
      toast.success(t('mkt_global_products.deleted'));
      setDeleteId(null);
      refresh();
    } catch (err) {
      toast.error((err as Error)?.message || t('mkt_global_products.error'));
    } finally { setDeleting(false); }
  };

  // Excel import — the file is converted to CSV and the server validates each row (FR-18).
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const { rows, csv } = await readProductsExcelWithCsv(file);
      setImportRows(rows);
      setImportCsv(csv);
    } catch (err) {
      toast.error((err as Error)?.message || t('mkt_global_products.excelReadFailed'));
    }
  };

  const closeImport = () => { setImportRows(null); setImportCsv(null); };

  const executeImport = async () => {
    if (!importCsv) return;
    setImporting(true);
    try {
      const body = new FormData();
      body.append('file', new Blob([importCsv], { type: 'text/csv' }), 'import.csv');
      const { data: result, error } = await apiClient.POST('/admin/global-products/import', { body: body as unknown as never });
      if (error) throw new Error(apiErrorMessage(error, t('mkt_global_products.importError')));
      const errors = result?.errors ?? [];
      if (errors.length > 0) {
        toast.error(t('mkt_global_products.importPartial', { imported: result?.imported ?? 0, errors: errors.length, row: errors[0].row }));
      } else {
        toast.success(t('mkt_global_products.imported', { imported: result?.imported ?? 0 }));
      }
      closeImport();
      refresh();
    } catch (err) {
      toast.error((err as Error)?.message || t('mkt_global_products.importError'));
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder={t('mkt_global_products.searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('mkt_global_products.allCategories')}</SelectItem>
            {filterCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        {guruhOptions.length > 0 && (
          <Select value={guruhFilter} onValueChange={setGuruhFilter}>
            <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('mkt_global_products.allGroups')}</SelectItem>
              {guruhOptions.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="flex gap-2 ml-auto">
          <Button size="sm" variant="outline" onClick={() => downloadProductTemplate('global-products-template.xlsx')}>
            <Download size={14} className="mr-1" /> {t('mkt_global_products.template')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
            <Upload size={14} className="mr-1" /> {t('mkt_global_products.excelImport')}
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileSelect} />
          <Button size="sm" onClick={() => { setEditingId(null); setForm(emptyProductForm); setShowForm(true); }}>
            <Plus size={14} className="mr-1" /> {t('mkt_global_products.add')}
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{t('mkt_global_products.totalCount', { n: total.toLocaleString(locale) })}</p>

      {isLoading ? (
        <div className="text-muted-foreground py-8 text-center">{t('mkt_global_products.loading')}</div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">SKU</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mkt_global_products.colName')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mkt_global_products.colBrand')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mkt_global_products.colAttribute')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mkt_global_products.colCategory')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mkt_global_products.mxikCode')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mkt_global_products.mxikName')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mkt_global_products.barcode')}</th>
                  <th className="text-right text-xs font-medium text-muted-foreground p-3">{t('mkt_global_products.colPrice')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mkt_global_products.colUnit')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mkt_global_products.colTenors')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('mkt_global_products.colMurabaha')}</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr><td colSpan={13} className="p-8 text-center text-muted-foreground">{t('mkt_global_products.notFound')}</td></tr>
                ) : products.map(p => (
                  <tr key={p.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="p-3 text-sm text-muted-foreground font-mono">{p.sku}</td>
                    <td className="p-3 text-sm text-foreground font-medium max-w-[240px] truncate" title={p.name}>{p.name}</td>
                    <td className="p-3 text-sm text-muted-foreground max-w-[140px] truncate" title={p.brend_nomi || ''}>{p.brend_nomi || '—'}</td>
                    <td className="p-3 text-sm text-muted-foreground max-w-[160px] truncate" title={p.atribut_nomi || ''}>{p.atribut_nomi || '—'}</td>
                    <td className="p-3 text-sm text-muted-foreground">{p.category || '—'}</td>
                    <td className="p-3 text-xs text-muted-foreground font-mono">{p.mhik || '—'}</td>
                    <td className="p-3 text-sm text-muted-foreground max-w-[220px] truncate" title={p.mxik_nomi || ''}>{p.mxik_nomi || '—'}</td>
                    <td className="p-3 text-xs text-muted-foreground font-mono">{p.barcode || '—'}</td>
                    <td className="p-3 text-sm text-foreground text-right font-mono">{formatUZS(Number(p.sale_price))}</td>
                    <td className="p-3 text-sm text-muted-foreground">{p.unit}</td>
                    <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{formatTenors(parseTenors(p.allowed_tenors))}</td>
                    <td className="p-3">
                      <Badge variant={p.is_murabaha_eligible ? 'default' : 'secondary'}>
                        {p.is_murabaha_eligible ? t('mkt_global_products.yes') : t('mkt_global_products.no')}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setInfoProduct(p)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground" title={t('mkt_global_products.mxikInfo')}><Info size={14} /></button>
                        <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground"><Edit2 size={14} /></button>
                        <button onClick={() => setDeleteId(p.id)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PaginationControls
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={PAGE_SIZE}
        onPrev={() => setPage(p => Math.max(1, p - 1))}
        onNext={() => setPage(p => Math.min(totalPages, p + 1))}
        hasPrev={page > 1}
        hasNext={page < totalPages}
      />

      <Dialog open={showForm} onOpenChange={v => { if (!v) closeForm(); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? t('mkt_global_products.editTitle') : t('mkt_global_products.newTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder={t('mkt_global_products.phName')} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <Input placeholder={t('mkt_global_products.phSku')} value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} disabled={!!editingId} />
            <Input placeholder={t('mkt_global_products.phBrand')} value={form.brend_nomi} onChange={e => setForm(f => ({ ...f, brend_nomi: e.target.value }))} />
            <Input placeholder={t('mkt_global_products.colAttribute')} value={form.atribut_nomi} onChange={e => setForm(f => ({ ...f, atribut_nomi: e.target.value }))} />
            <Input placeholder={t('mkt_global_products.phMxikCode')} maxLength={17} value={form.mhik} onChange={e => setForm(f => ({ ...f, mhik: e.target.value }))} />
            <Input placeholder={t('mkt_global_products.mxikName')} value={form.mxik_nomi} onChange={e => setForm(f => ({ ...f, mxik_nomi: e.target.value }))} />
            <Input placeholder={t('mkt_global_products.barcode')} value={form.barcode} onChange={e => setForm(f => ({ ...f, barcode: e.target.value }))} />
            <div className="space-y-1.5">
              <Label>{t('mkt_global_products.colCategory')}</Label>
              <Select value={form.category || '__none__'} onValueChange={v => setForm(f => ({ ...f, category: v === '__none__' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder={t('mkt_global_products.phCategory')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('mkt_global_products.notSelected')}</SelectItem>
                  {activeCategories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                  {uniqueCategories.filter(c => !activeCategories.includes(c)).map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input placeholder={t('mkt_global_products.colPrice')} type="number" value={form.sale_price} onChange={e => setForm(f => ({ ...f, sale_price: e.target.value }))} />
            <Select value={form.unit} onValueChange={v => setForm(f => ({ ...f, unit: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRODUCT_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="space-y-2">
              <Label>{t('mkt_global_products.tenorsLabel')}</Label>
              <div className="flex flex-wrap gap-3">
                {PRODUCT_TENORS.map(ten => {
                  const selected = parseTenors(form.allowed_tenors).includes(ten);
                  return (
                    <label key={ten} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${selected ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground'}`}>
                      <Checkbox checked={selected} onCheckedChange={v => setForm(f => {
                        const current = parseTenors(f.allowed_tenors);
                        return { ...f, allowed_tenors: parseTenors(v ? [...current, ten] : current.filter(x => x !== ten)) };
                      })} />
                      {t('mkt_global_products.monthsN', { n: ten })}
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="gp-murabaha" checked={form.is_murabaha_eligible} onCheckedChange={(v) => setForm(f => ({ ...f, is_murabaha_eligible: !!v }))} />
              <label htmlFor="gp-murabaha" className="text-sm text-foreground">{t('mkt_global_products.murabahaEligible')}</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeForm}>{t('mkt_global_products.cancel')}</Button>
            <Button onClick={saveProduct} disabled={!form.name || !form.sku || saving}>
              {saving ? <><Loader2 size={14} className="animate-spin mr-1" /> {t('mkt_global_products.saving')}</> : t('mkt_global_products.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!infoProduct} onOpenChange={v => { if (!v) setInfoProduct(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('mkt_global_products.mxikInfo')}</DialogTitle></DialogHeader>
          {infoProduct && (
            <div className="space-y-2 text-sm">
              <p className="font-medium text-foreground">{infoProduct.name}</p>
              {([
                [t('mkt_global_products.mxikCode'), infoProduct.mhik],
                [t('mkt_global_products.mxikName'), infoProduct.mxik_nomi],
                [t('mkt_global_products.barcode'), infoProduct.barcode],
                [t('mkt_global_products.infoGroup'), infoProduct.guruh_nomi],
                [t('mkt_global_products.infoClass'), infoProduct.sinf_nomi],
                [t('mkt_global_products.infoPosition'), infoProduct.pozitsiya_nomi],
                [t('mkt_global_products.infoSubposition'), infoProduct.subpozitsiya_nomi],
                [t('mkt_global_products.colBrand'), infoProduct.brend_nomi],
                [t('mkt_global_products.colAttribute'), infoProduct.atribut_nomi],
                [t('mkt_global_products.infoBaseUnit'), infoProduct.unit],
                [t('mkt_global_products.infoAllowedUnits'), ((infoProduct.allowed_units as string[] | null) || []).join(', ')],
              ] as Array<[string, string | null | undefined]>).map(([label, value]) => (
                <div key={label} className="flex gap-3 border-b border-border/60 pb-1.5 last:border-0">
                  <span className="w-48 shrink-0 text-muted-foreground text-xs">{label}</span>
                  <span className="text-foreground break-words">{value || '—'}</span>
                </div>
              ))}
              {infoProduct.is_service && <Badge variant="secondary">{t('mkt_global_products.service')}</Badge>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setInfoProduct(null)}>{t('mkt_global_products.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('mkt_global_products.deleteTitle')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('mkt_global_products.deleteConfirm')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>{t('mkt_global_products.cancel')}</Button>
            <Button variant="destructive" onClick={deleteProduct} disabled={deleting}>
              {deleting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
              {t('mkt_global_products.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProductImportDialog
        open={importRows !== null}
        rows={importRows}
        importing={importing}
        onClose={closeImport}
        onConfirm={executeImport}
      />
    </div>
  );
}
